// Natural-language capture (Greek + English), deterministic and offline.
//   "ραντεβού με την Άννα αύριο στις 10:30 για review"  → appointment · Άννα Σταύρου · tomorrow 10:30 · "review"
//   "call George Friday 3pm about renewal"              → call · George … · Friday 15:00 · "renewal"
//   "θύμισέ μου να στείλω SEPA στον Μάριο σε 3 μέρες"   → follow-up · Μάριος … · +3 days
// Every recognised fragment is reported as a span so the UI can show exactly what was understood.
import { addDays, searchKey, type Day } from '@vero/domain';

export type CaptureKind = 'appointment' | 'call' | 'followup' | 'task' | 'note' | 'case';
export interface CapturePerson { id: string; displayName: string; firstName?: string | null; lastName?: string | null }
export interface CaptureSpan { start: number; end: number; type: 'kind' | 'date' | 'time' | 'person' | 'duration' }
export interface CaptureResult {
  kind: CaptureKind;
  kindExplicit: boolean;
  personId?: string;
  candidates: { id: string; name: string; score: number }[];
  date?: Day;
  time?: string;
  durationMin?: number;
  title: string;
  spans: CaptureSpan[];
  confidence: number;
}

const WEEKDAYS: [RegExp, number][] = [
  [/^(κυριακη|sunday|sun)$/, 0], [/^(δευτερα|monday|mon)$/, 1], [/^(τριτη|tuesday|tue|tues)$/, 2], [/^(τεταρτη|wednesday|wed)$/, 3],
  [/^(πεμπτη|thursday|thu|thurs)$/, 4], [/^(παρασκευη|friday|fri)$/, 5], [/^(σαββατο|saturday|sat)$/, 6],
];
const MONTHS: [RegExp, number][] = [
  [/^(ιανουαριου|ιανουαριος|ιαν|january|jan)$/, 1], [/^(φεβρουαριου|φεβρουαριος|φεβ|february|feb)$/, 2], [/^(μαρτιου|μαρτιος|μαρ|march|mar)$/, 3],
  [/^(απριλιου|απριλιος|απρ|april|apr)$/, 4], [/^(μαιου|μαιος|μαι|may)$/, 5], [/^(ιουνιου|ιουνιος|ιουν|june|jun)$/, 6], [/^(ιουλιου|ιουλιος|ιουλ|july|jul)$/, 7],
  [/^(αυγουστου|αυγουστος|αυγ|august|aug)$/, 8], [/^(σεπτεμβριου|σεπτεμβριος|σεπ|september|sep|sept)$/, 9], [/^(οκτωβριου|οκτωβριος|οκτ|october|oct)$/, 10],
  [/^(νοεμβριου|νοεμβριος|νοε|november|nov)$/, 11], [/^(δεκεμβριου|δεκεμβριος|δεκ|december|dec)$/, 12],
];
const KINDS: [CaptureKind, RegExp][] = [
  ['appointment', /^(ραντεβου|συναντηση|συναντησης|meeting|appointment|appt|meet)$/],
  ['call', /^(κληση|καλεσε|τηλεφωνο|τηλεφωνησε|call|phone|ring)$/],
  ['case', /^(αιτηση|application)$/],
  ['followup', /^(υπενθυμιση|θυμισε|θυμισου|remind|reminder|followup|follow-up)$/],
  ['note', /^(σημειωση|note)$/],
  ['task', /^(εργασια|task|todo)$/],
];
const FILLER = new Set(['με', 'τον', 'την', 'του', 'της', 'το', 'τη', 'στον', 'στην', 'στο', 'στη', 'για', 'στις', 'στη', 'και', 'να', 'μου', 'σε', 'απο', 'κ', 'κα', 'κος', 'κυρια', 'κυριο', 'with', 'for', 'at', 'on', 'the', 'to', 'a', 'an', 'about', 'me', 'my', 'in', 'mr', 'mrs', 'ms', 'this', 'next', 'επομενη', 'επομενο', 'αυτη', 'up', 'follow', '@']);

interface Tok { raw: string; key: string; start: number; end: number; used?: CaptureSpan['type'] }

function tokenize(text: string): Tok[] {
  const out: Tok[] = [];
  const re = /[\p{L}\p{N}][\p{L}\p{N}:.\/'’-]*|@/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const raw = m[0].replace(/[.,;:!?’']+$/, '');
    if (!raw) continue;
    out.push({ raw, key: searchKey(raw), start: m.index, end: m.index + raw.length });
  }
  return out;
}

/** Grammatical forms of a (normalised) Greek/English name: Γιώργος → γιωργος, γιωργο, γιωργου, γιωργε. */
export function declensions(k: string): Set<string> {
  const out = new Set([k]);
  const add = (base: string, ends: string[]) => ends.forEach((e) => out.add(base + e));
  if (/ος$/.test(k)) add(k.slice(0, -2), ['ο', 'ου', 'ε']);
  else if (/ης$/.test(k)) add(k.slice(0, -2), ['η']);
  else if (/ας$/.test(k)) add(k.slice(0, -2), ['α']);
  else if (/[αη]$/.test(k)) add(k, ['ς']);
  else if (/[a-z]$/.test(k)) add(k, ['s']);
  return out;
}

/** strip one Greek/English inflection ending for fuzzy name matching (Γιώργος/Γιώργο/Γιώργου → γιωργ) */
export function stem(k: string): string {
  if (k.length <= 4) return k;
  return k.replace(/(ους|ος|ης|ας|ου|ων|ες|ον|α|η|ο|ε|ι|υ|ς|'s|s)$/, '');
}

function nextWeekday(today: Day, wd: number): Day {
  const cur = new Date(`${today}T12:00:00Z`).getUTCDay();
  let d = (wd - cur + 7) % 7;
  if (d === 0) d = 7;
  return addDays(today, d);
}
function mkDate(today: Day, day: number, month: number, year?: number): Day | undefined {
  const y0 = Number(today.slice(0, 4));
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const mk = (y: number) => { const dt = new Date(Date.UTC(y, month - 1, day)); return dt.getUTCMonth() === month - 1 ? dt.toISOString().slice(0, 10) : undefined; };
  if (year) return mk(year < 100 ? 2000 + year : year);
  const d = mk(y0);
  return d && d < today ? mk(y0 + 1) : d;
}
const NUM_WORDS: Record<string, number> = { ενα: 1, μια: 1, μια_: 1, one: 1, a: 1, δυο: 2, two: 2, τρεις: 3, τρια: 3, three: 3, τεσσερις: 4, four: 4, πεντε: 5, five: 5, δεκα: 10, ten: 10 };

export function parseCapture(text: string, opts: { today: Day; people: CapturePerson[] }): CaptureResult {
  const toks = tokenize(text);
  const spans: CaptureSpan[] = [];
  const mark = (from: number, to: number, type: CaptureSpan['type']) => { for (let i = from; i <= to; i++) toks[i]!.used = type; spans.push({ start: toks[from]!.start, end: toks[to]!.end, type }); };
  let kind: CaptureKind | undefined;
  let date: Day | undefined, time: string | undefined, durationMin: number | undefined;
  const today = opts.today;

  for (let i = 0; i < toks.length; i++) {
    const t = toks[i]!;
    if (t.used) continue;
    const k = t.key;
    // kind (first explicit keyword wins); "πάρε τηλέφωνο", "follow up"
    if (!kind) {
      const hit = KINDS.find(([, re]) => re.test(k));
      if (hit) { kind = hit[0]; mark(i, i, 'kind'); if (k === 'follow' && toks[i + 1]?.key === 'up') mark(i, i + 1, 'kind'); continue; }
      if ((k === 'παρε' || k === 'παρ') && toks[i + 1] && /^τηλ/.test(toks[i + 1]!.key)) { kind = 'call'; mark(i, i + 1, 'kind'); i++; continue; }
      if (k === 'follow' && toks[i + 1]?.key === 'up') { kind = 'followup'; mark(i, i + 1, 'kind'); i++; continue; }
    }
    // relative dates
    if (!date) {
      if (k === 'σημερα' || k === 'today') { date = today; mark(i, i, 'date'); continue; }
      if (k === 'αυριο' || k === 'tomorrow' || k === 'tmrw') { date = addDays(today, 1); mark(i, i, 'date'); continue; }
      if (k === 'μεθαυριο') { date = addDays(today, 2); mark(i, i, 'date'); continue; }
      const wd = WEEKDAYS.find(([re]) => re.test(k));
      if (wd) { date = nextWeekday(today, wd[1]); mark(i, i, 'date'); continue; }
      // "σε 3 μέρες", "in 2 weeks", "σε ένα μήνα"
      if ((k === 'σε' || k === 'in') && toks[i + 1] && toks[i + 2]) {
        const n = /^\d+$/.test(toks[i + 1]!.key) ? Number(toks[i + 1]!.key) : NUM_WORDS[toks[i + 1]!.key];
        const unit = toks[i + 2]!.key;
        if (n) {
          const days = /^(μερες|μερα|ημερες|days?|d)$/.test(unit) ? n : /^(εβδομαδες|εβδομαδα|weeks?|w)$/.test(unit) ? n * 7 : /^(μηνες|μηνα|months?)$/.test(unit) ? n * 30 : 0;
          if (days) { date = addDays(today, days); mark(i, i + 2, 'date'); i += 2; continue; }
        }
      }
      // "next week", "την επόμενη εβδομάδα"
      if ((k === 'next' || k === 'επομενη' || k === 'αλλη') && toks[i + 1] && /^(week|εβδομαδα)$/.test(toks[i + 1]!.key)) { date = addDays(today, 7); mark(i, i + 1, 'date'); i++; continue; }
      // "5/10", "05/10/2026", "5-10", "5.10.26"
      const dm = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/.exec(t.raw);
      if (dm && !(toks[i - 1] && /^(στις|at|@)$/.test(toks[i - 1]!.key))) {
        const d = mkDate(today, +dm[1]!, +dm[2]!, dm[3] ? +dm[3] : undefined);
        if (d) { date = d; mark(i, i, 'date'); continue; }
      }
      // "5 Οκτωβρίου", "5 oct 2026", "October 5"
      if (/^\d{1,2}$/.test(k) && toks[i + 1]) {
        const mo = MONTHS.find(([re]) => re.test(toks[i + 1]!.key));
        if (mo) { const y = toks[i + 2] && /^\d{4}$/.test(toks[i + 2]!.key) ? +toks[i + 2]!.key : undefined; const d = mkDate(today, +k, mo[1], y); if (d) { date = d; mark(i, y ? i + 2 : i + 1, 'date'); i += y ? 2 : 1; continue; } }
      }
      const mo = MONTHS.find(([re]) => re.test(k));
      if (mo && toks[i + 1] && /^\d{1,2}$/.test(toks[i + 1]!.key)) { const d = mkDate(today, +toks[i + 1]!.key, mo[1]); if (d) { date = d; mark(i, i + 1, 'date'); i++; continue; } }
    }
    // times: "10:30", "στις 10", "at 3pm", "3 μμ", "15.00" after στις/at
    if (!time) {
      const prefixed = /^(στις|at|@)$/.test(k);
      const j = prefixed ? i + 1 : i;
      const tt = toks[j];
      if (tt) {
        const m = /^(\d{1,2})(?:[:.](\d{2}))?(am|pm|πμ|μμ)?$/.exec(tt.key);
        const colon = /^\d{1,2}:\d{2}$/.test(tt.key);
        if (m && (prefixed || colon || m[3])) {
          let h = +m[1]!; const min = m[2] ? +m[2] : 0;
          let suffix = m[3];
          let end = j;
          const nx = toks[j + 1]?.key;
          if (!suffix && nx && /^(am|pm|πμ|μμ|π\.μ|μ\.μ|πρωι|απογευμα|βραδυ)$/.test(nx.replace(/\.$/, ''))) { suffix = nx; end = j + 1; }
          if (suffix && /^(pm|μμ|μ\.μ|απογευμα|βραδυ)/.test(suffix) && h < 12) h += 12;
          if (suffix && /^(am|πμ|π\.μ)/.test(suffix) && h === 12) h = 0;
          if (!suffix && prefixed && h >= 1 && h <= 7) h += 12; // "στις 5" in business context = 17:00
          if (h <= 23 && min <= 59) { time = `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`; mark(i, end, 'time'); i = end; continue; }
        }
      }
      if (/^(πρωι|morning)$/.test(k)) { time = '09:00'; mark(i, i, 'time'); continue; }
      if (/^(μεσημερι|noon|lunchtime)$/.test(k)) { time = '12:30'; mark(i, i, 'time'); continue; }
      if (/^(απογευμα|afternoon)$/.test(k)) { time = '17:00'; mark(i, i, 'time'); continue; }
      if (/^(βραδυ|evening)$/.test(k)) { time = '19:00'; mark(i, i, 'time'); continue; }
    }
    // duration: "για 30 λεπτά", "for 1 hour", "45min"
    if (!durationMin) {
      const n = /^\d+$/.test(k) ? +k : undefined;
      const unit = toks[i + 1]?.key;
      if (n && unit && /^(λεπτα|λεπτο|min|mins|minutes|minute|λ)$/.test(unit)) { durationMin = n; mark(i, i + 1, 'duration'); i++; continue; }
      if (n && unit && /^(ωρα|ωρες|hour|hours|h)$/.test(unit)) { durationMin = n * 60; mark(i, i + 1, 'duration'); i++; continue; }
      const c = /^(\d+)(min|m|h)$/.exec(k);
      if (c) { durationMin = c[2] === 'h' ? +c[1]! * 60 : +c[1]!; mark(i, i, 'duration'); continue; }
    }
  }

  // people: score each person by how many of its name parts appear (inflection-tolerant)
  const free = toks.map((t, i) => ({ t, i })).filter(({ t }) => !t.used && t.key.length >= 2 && !FILLER.has(t.key) && !/^\d/.test(t.key));
  const scored = opts.people.map((p) => {
    const parts = [p.firstName, p.lastName, ...p.displayName.split(/\s+/)].filter((x): x is string => !!x).map((x) => searchKey(x)).filter((x, i, a) => x.length >= 2 && a.indexOf(x) === i);
    let score = 0; const hits = new Set<number>();
    for (const part of parts) {
      const ps = stem(part);
      const forms = declensions(part);
      for (const { t, i } of free) {
        if (t.key === part) { score += 1; hits.add(i); }
        else if (forms.has(t.key)) { score += 0.95; hits.add(i); }
        else if (ps.length >= 3 && stem(t.key) === ps) { score += 0.6; hits.add(i); }
        else if (ps.length >= 4 && t.key.length >= 4 && (t.key.startsWith(ps) || ps.startsWith(stem(t.key)))) { score += 0.45; hits.add(i); }
      }
    }
    return { p, score, hits };
  }).filter((x) => x.score >= 0.45).sort((a, b) => b.score - a.score || a.p.displayName.length - b.p.displayName.length);
  const best = scored[0];
  const unique = best && (!scored[1] || best.score - scored[1].score >= 0.3);
  if (best && unique) {
    const idx = [...best.hits].sort((a, b) => a - b);
    for (const i of idx) mark(i, i, 'person');
  }

  // title: what's left, minus filler at the edges
  const rest = toks.filter((t) => !t.used);
  while (rest.length && FILLER.has(rest[0]!.key)) rest.shift();
  while (rest.length && FILLER.has(rest[rest.length - 1]!.key)) rest.pop();
  const title = rest.length ? text.slice(rest[0]!.start, rest[rest.length - 1]!.end).replace(/\s+/g, ' ').trim() : '';

  const kindExplicit = !!kind;
  if (!kind) kind = time && date ? 'appointment' : date ? 'followup' : 'note';
  if (kind === 'appointment' && date && !time) time = '10:00';
  const confidence = Math.min(1, (kindExplicit ? 0.35 : 0.1) + (date ? 0.25 : 0) + (time ? 0.1 : 0) + (best && unique ? 0.3 : 0));
  return {
    kind, kindExplicit, personId: best && unique ? best.p.id : undefined,
    candidates: scored.slice(0, 4).map((x) => ({ id: x.p.id, name: x.p.displayName, score: Math.round(x.score * 100) / 100 })),
    date, time, durationMin, title, spans: spans.sort((a, b) => a.start - b.start), confidence,
  };
}
